/**
 * Cloudflare Workers API Client
 * 
 * Unified API client for all Workers endpoints
 * Provides database operations, RPC calls, and storage functionality
 */

// ===== TYPE DEFINITIONS =====

/**
 * Generic database filters
 */
type DbFilters = Record<string, string | number | boolean | null>;

/**
 * Database record with unknown structure
 */
type DbRecord = Record<string, unknown>;

/**
 * RPC function parameters
 */
type RpcParams = Record<string, unknown>;

/**
 * Storage upload response
 */
interface StorageUploadResponse {
  path: string;
  url: string;
}

/**
 * Channel subscription callback
 */
type SubscriptionCallback = (status: string, error: Error | null) => void;

/**
 * Unsubscribe function
 */
interface UnsubscribeResult {
  unsubscribe: () => void;
}

// ===== API CLIENT =====

const WORKERS_API_URL = import.meta.env.VITE_API_URL || 'https://api.trafomatic.reg2dep.net';

// Helper to get auth token
function getAuthToken(): string | null {
  return localStorage.getItem('workers_access_token');
}

// Helper to get current user data from localStorage
function getCurrentUserData() {
  const userData = localStorage.getItem('workers_user_data');
  return userData ? JSON.parse(userData) : null;
}

// Helper for API requests
async function fetchAPI<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  
  // ✅ CRITICAL FIX: Детальніше логування для діагностики 401 помилок
  if (!token && endpoint !== '/api/auth/telegram') {
    console.warn(`[Workers API] ⚠️ No auth token found for request to ${endpoint}`);
  }
  
  const response = await fetch(`${WORKERS_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    
    // ✅ CRITICAL FIX: Handle 401 errors intelligently to prevent auth loops
    if (response.status === 401) {
      console.error(`[Workers API] 🔒 401 Unauthorized on ${endpoint}:`, {
        hasToken: !!token,
        tokenPrefix: token ? token.substring(0, 10) + '...' : 'none',
        errorText,
        endpoint,
      });
      
      // 🔥 CRITICAL: Only clear tokens if we HAD a token that was rejected
      // If there's NO token, it means we're still in initial auth phase - don't clear anything!
      if (token) {
        console.warn(`[Workers API] 🔄 Clearing invalid tokens, user will need to re-authenticate`);
        localStorage.removeItem('workers_access_token');
        localStorage.removeItem('workers_refresh_token');
        localStorage.removeItem('workers_user');
        
        // Notify auth context about sign out
        window.dispatchEvent(new CustomEvent('workers-auth-signout'));
      } else {
        console.warn(`[Workers API] ⚠️ 401 error but no token present - likely premature API call during auth initialization`);
      }
    }
    
    throw new Error(`API Error (${response.status}): ${errorText}`);
  }

  return await response.json();
}

/**
 * InsertBuilder for INSERT operations
 */
class InsertBuilder<T = DbRecord> {
  private table: string;
  private data: DbRecord | DbRecord[];
  private isUpsert: boolean;
  private selectColumns: string = '*';

  constructor(table: string, data: DbRecord | DbRecord[], isUpsert = false) {
    this.table = table;
    this.data = data;
    this.isUpsert = isUpsert;
  }

  select(columns: string = '*'): this {
    this.selectColumns = columns;
    return this;
  }

  async then<TResult = T>(
    onfulfilled?: ((value: { data: T | T[] | null; error: Error | null }) => TResult | PromiseLike<TResult>) | null,
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
  ): Promise<TResult> {
    try {
      const endpoint = this.isUpsert ? `/api/db/${this.table}/upsert` : `/api/db/${this.table}`;
      const result = await fetchAPI<T | T[]>(endpoint, {
        method: 'POST',
        body: JSON.stringify(this.data),
      });
      const response = { data: result, error: null };
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    } catch (error) {
      const response = { data: null, error: error instanceof Error ? error : new Error(String(error)) };
      if (onrejected) {
        return onrejected(error);
      }
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    }
  }
}

/**
 * UpdateBuilder for UPDATE operations
 */
class UpdateBuilder<T = DbRecord> {
  private table: string;
  private data: DbRecord;
  private filters: Array<{ type: string; column?: string; value?: unknown; condition?: string }>;

  constructor(table: string, data: DbRecord, filters: Array<{ type: string; column?: string; value?: unknown; condition?: string }> = []) {
    this.table = table;
    this.data = data;
    this.filters = filters;
  }

  eq(column: string, value: string | number | boolean): this {
    this.filters.push({ type: 'eq', column, value });
    return this;
  }

  async then<TResult = T>(
    onfulfilled?: ((value: { data: T | null; error: Error | null }) => TResult | PromiseLike<TResult>) | null,
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
  ): Promise<TResult> {
    try {
      // For now, use first eq filter as ID
      const idFilter = this.filters.find(f => f.type === 'eq');
      if (!idFilter || !idFilter.value) {
        throw new Error('UPDATE requires .eq() filter');
      }
      
      const result = await fetchAPI<T>(`/api/db/${this.table}/${idFilter.value}`, {
        method: 'PATCH',
        body: JSON.stringify(this.data),
      });
      const response = { data: result, error: null };
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    } catch (error) {
      const response = { data: null, error: error instanceof Error ? error : new Error(String(error)) };
      if (onrejected) {
        return onrejected(error);
      }
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    }
  }
}

/**
 * DeleteBuilder for DELETE operations
 */
class DeleteBuilder<T = DbRecord> {
  private table: string;
  private filters: Array<{ type: string; column?: string; value?: unknown; condition?: string }>;

  constructor(table: string, filters: Array<{ type: string; column?: string; value?: unknown; condition?: string }> = []) {
    this.table = table;
    this.filters = filters;
  }

  eq(column: string, value: string | number | boolean): this {
    this.filters.push({ type: 'eq', column, value });
    return this;
  }

  async then<TResult = T>(
    onfulfilled?: ((value: { data: null; error: Error | null }) => TResult | PromiseLike<TResult>) | null,
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
  ): Promise<TResult> {
    try {
      // For now, use first eq filter as ID
      const idFilter = this.filters.find(f => f.type === 'eq');
      if (!idFilter || !idFilter.value) {
        throw new Error('DELETE requires .eq() filter');
      }
      
      await fetchAPI(`/api/db/${this.table}/${idFilter.value}`, {
        method: 'DELETE',
      });
      const response = { data: null, error: null };
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    } catch (error) {
      const response = { data: null, error: error instanceof Error ? error : new Error(String(error)) };
      if (onrejected) {
        return onrejected(error);
      }
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    }
  }
}

/**
 * Query Builder for Workers-compatible API
 * Provides chainable query methods for database operations
 */
class QueryBuilder<T = DbRecord> {
  private table: string;
  private selectColumns: string = '*';
  private filters: Array<{ type: string; column?: string; value?: unknown; condition?: string }> = [];
  private orderBy: Array<{ column: string; ascending: boolean }> = [];
  private limitCount?: number;
  private rangeStart?: number;
  private rangeEnd?: number;
  private isSingleResult = false;

  constructor(table: string) {
    this.table = table;
  }

  /**
   * SELECT with column specification
   */
  select(columns: string = '*'): this {
    this.selectColumns = columns;
    return this;
  }

  /**
   * Filter by equality
   */
  eq(column: string, value: string | number | boolean): this {
    this.filters.push({ type: 'eq', column, value });
    return this;
  }

  /**
   * Filter by inequality
   */
  neq(column: string, value: string | number | boolean): this {
    this.filters.push({ type: 'neq', column, value });
    return this;
  }

  /**
   * Greater than or equal
   */
  gte(column: string, value: string | number): this {
    this.filters.push({ type: 'gte', column, value });
    return this;
  }

  /**
   * Less than or equal
   */
  lte(column: string, value: string | number): this {
    this.filters.push({ type: 'lte', column, value });
    return this;
  }

  /**
   * Greater than
   */
  gt(column: string, value: string | number): this {
    this.filters.push({ type: 'gt', column, value });
    return this;
  }

  /**
   * Less than
   */
  lt(column: string, value: string | number): this {
    this.filters.push({ type: 'lt', column, value });
    return this;
  }

  /**
   * LIKE filter (case-sensitive)
   */
  like(column: string, pattern: string): this {
    this.filters.push({ type: 'like', column, value: pattern });
    return this;
  }

  /**
   * ILIKE filter (case-insensitive)
   */
  ilike(column: string, pattern: string): this {
    this.filters.push({ type: 'ilike', column, value: pattern });
    return this;
  }

  /**
   * IN filter (value in array)
   */
  in(column: string, values: Array<string | number>): this {
    this.filters.push({ type: 'in', column, value: values });
    return this;
  }

  /**
   * OR filter (complex condition)
   */
  or(condition: string): this {
    this.filters.push({ type: 'or', condition });
    return this;
  }

  /**
   * Order results
   */
  order(column: string, options?: { ascending?: boolean }): this {
    this.orderBy.push({ column, ascending: options?.ascending ?? true });
    return this;
  }

  /**
   * Limit number of results
   */
  limit(count: number): this {
    this.limitCount = count;
    return this;
  }

  /**
   * Range of results (pagination)
   */
  range(start: number, end: number): this {
    this.rangeStart = start;
    this.rangeEnd = end;
    return this;
  }

  /**
   * Return single result
   */
  single(): this {
    this.isSingleResult = true;
    return this;
  }

  /**
   * INSERT data
   */
  insert(data: DbRecord | DbRecord[]): InsertBuilder<T> {
    return new InsertBuilder<T>(this.table, data);
  }

  /**
   * UPDATE data
   */
  update(data: DbRecord): UpdateBuilder<T> {
    return new UpdateBuilder<T>(this.table, data, this.filters);
  }

  /**
   * DELETE data
   */
  delete(): DeleteBuilder<T> {
    return new DeleteBuilder<T>(this.table, this.filters);
  }

  /**
   * UPSERT data (insert or update)
   */
  upsert(data: DbRecord | DbRecord[]): InsertBuilder<T> {
    return new InsertBuilder<T>(this.table, data, true);
  }

  /**
   * Execute query and return results
   */
  async then<TResult = T>(
    onfulfilled?: ((value: { data: T | T[] | null; error: Error | null }) => TResult | PromiseLike<TResult>) | null,
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null
  ): Promise<TResult> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('select', this.selectColumns);

      // Add filters
      this.filters.forEach((filter, idx) => {
        if (filter.type === 'or') {
          params.append('or', filter.condition || '');
        } else if (filter.column) {
          params.append(`filter_${idx}`, JSON.stringify(filter));
        }
      });

      // Add ordering
      if (this.orderBy.length > 0) {
        params.append('order', JSON.stringify(this.orderBy));
      }

      // Add limit
      if (this.limitCount !== undefined) {
        params.append('limit', String(this.limitCount));
      }

      // Add range
      if (this.rangeStart !== undefined && this.rangeEnd !== undefined) {
        params.append('range', `${this.rangeStart}-${this.rangeEnd}`);
      }

      const query = `?${params.toString()}`;
      const results = await fetchAPI<T[]>(`/api/db/${this.table}${query}`);

      const data = this.isSingleResult ? (results[0] || null) : results;
      const response = { data, error: null };

      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    } catch (error) {
      const response = { data: null, error: error instanceof Error ? error : new Error(String(error)) };
      if (onrejected) {
        return onrejected(error);
      }
      return (onfulfilled ? onfulfilled(response) : response) as TResult;
    }
  }
}

/**
 * Database Operations (database queries and mutations)
 */
export const db = {
  /**
   * Start a query builder for SELECT operations
   */
  from<T = DbRecord>(table: string): QueryBuilder<T> {
    return new QueryBuilder<T>(table);
  },

  /**
   * Direct SELECT операції (deprecated, use from().select() instead)
   */
  select: async <T = DbRecord>(table: string, filters?: DbFilters): Promise<T[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, String(value));
      });
    }
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchAPI<T[]>(`/api/db/${table}${query}`);
  },

  /**
   * INSERT операції
   */
  insert: async <T = DbRecord>(table: string, data: DbRecord): Promise<T> => {
    return fetchAPI<T>(`/api/db/${table}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * UPDATE операції
   */
  update: async <T = DbRecord>(table: string, id: string | number, data: DbRecord): Promise<T> => {
    return fetchAPI<T>(`/api/db/${table}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * DELETE операції
   */
  delete: async (table: string, id: string | number): Promise<void> => {
    return fetchAPI(`/api/db/${table}/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * RPC Operations (remote procedure calls)
 */
export const rpc = async <T = unknown>(
  functionName: string,
  params?: RpcParams
): Promise<{ data: T | null; error: Error | null }> => {
  try {
    const data = await fetchAPI<T>(`/api/rpc/${functionName}`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
};

/**
 * Storage Operations (file upload and management)
 */
export const storage = {
  /**
   * Upload file
   */
  upload: async (
    bucket: string,
    path: string,
    file: File | Blob
  ): Promise<StorageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = getAuthToken();
    const response = await fetch(
      `${WORKERS_API_URL}/api/storage/${bucket}/${path}`,
      {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return await response.json();
  },

  /**
   * Get public URL
   */
  getPublicUrl: (bucket: string, path: string): string => {
    return `${WORKERS_API_URL}/api/storage/${bucket}/${path}`;
  },

  /**
   * Delete file
   */
  remove: async (bucket: string, path: string): Promise<void> => {
    const token = getAuthToken();
    const response = await fetch(
      `${WORKERS_API_URL}/api/storage/${bucket}/${path}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Delete failed: ${response.statusText}`);
    }
  },
};

/**
 * Legacy compatibility layer
 * Provides minimal compatibility with legacy database SDK patterns
 */
export const createCompatibilityClient = () => {
  return {
    from: <T = DbRecord>(table: string) => new QueryBuilder<T>(table),
    
    rpc: (functionName: string, params?: RpcParams) => rpc(functionName, params),
    
    auth: {
      getUser: async () => {
        const user = getCurrentUserData();
        return { 
          data: { user }, 
          error: null 
        };
      },
      getSession: async () => {
        const token = getAuthToken();
        const user = getCurrentUserData();
        return {
          data: {
            session: token ? { access_token: token, user } : null,
          },
          error: null,
        };
      },
    },
    
    storage: {
      from: (bucket: string) => ({
        upload: async (path: string, file: File | Blob) => {
          const result = await storage.upload(bucket, path, file);
          return { data: { path: result.path }, error: null };
        },
        getPublicUrl: (path: string) => ({ 
          data: { publicUrl: storage.getPublicUrl(bucket, path) } 
        }),
        remove: (paths: string[]) => Promise.all(paths.map(p => storage.remove(bucket, p))),
      }),
    },
    
    // 🚨 CRITICAL FIX: Mock .channel() to prevent runtime errors
    // Realtime is NOT available in Workers mode - this is a compatibility stub
    channel: (channelName: string) => {
      console.warn(`[Workers Compatibility] ⚠️ Realtime channel "${channelName}" called but Realtime is NOT available!`);
      console.warn('[Workers Compatibility] 💡 Suggestion: Remove realtime subscriptions or implement WebSocket/SSE alternative');
      
      // Return mock channel object to prevent crashes
      return {
        on: (...args: unknown[]) => {
          console.warn(`[Workers Compatibility] ⚠️ channel.on() called but ignored (no realtime support)`);
          return { subscribe: (): UnsubscribeResult => ({ unsubscribe: () => {} }) };
        },
        subscribe: (callback?: SubscriptionCallback) => {
          console.warn(`[Workers Compatibility] ⚠️ channel.subscribe() called but ignored (no realtime support)`);
          if (callback) callback('SUBSCRIBED', null);
          return { unsubscribe: () => {} };
        },
        unsubscribe: () => {
          console.warn(`[Workers Compatibility] ⚠️ channel.unsubscribe() called but ignored`);
          return Promise.resolve({ error: null });
        },
        send: (...args: unknown[]) => {
          console.warn(`[Workers Compatibility] ⚠️ channel.send() called but ignored (no realtime support)`);
          return Promise.resolve({ error: null });
        },
      };
    },
    
    removeChannel: (channel: unknown) => {
      console.warn('[Workers Compatibility] ⚠️ removeChannel() called but ignored (no realtime support)');
      return Promise.resolve({ error: null });
    },
  };
};

/**
 * Chat API helpers
 * Provides methods for chat operations
 */
export const chatApi = {
  /**
   * Get chat by ID with joined user data
   * Replaces: supabase.from('chats').select('*, user1:users!chats_user1_id_fkey(id, role), user2:users!chats_user2_id_fkey(id, role)').eq('id', chatId).single()
   */
  getChatWithUsers: async (chatId: string) => {
    try {
      const response = await fetchAPI<{ data: unknown }>(`/api/chats/${chatId}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  },
};

// Export default compatibility client for easy migration
export const workersClient = createCompatibilityClient();

// Default export
export default workersClient;
